import {MSALInfo} from '../types';
import {authentication} from '@microsoft/teams-js';
import withTimeout from './withTimeout';

/**
 * Ceiling on silent token acquisition (MSAL hidden-iframe flows and the Teams host
 * call are both capable of hanging without resolving). Token acquisition sits in
 * front of every authenticated request, so a hang here freezes the whole tab; a
 * bounded failure instead flows into the callers' existing null-token handling.
 */
export const SILENT_TOKEN_TIMEOUT_MS = 15_000;

const getAccessTokenImpl = async (
    clientId: string,
    tenantId: string,
    {instance, accounts}: MSALInfo,
    isRunningInTeams?: boolean
) => {
    const request = {
        scopes: [`${clientId}/.default`],
        account: accounts[0]
    };

    if (isRunningInTeams) {
        try {
            const token = await withTimeout(
                authentication.getAuthToken({tenantId}),
                SILENT_TOKEN_TIMEOUT_MS,
                'Teams token acquisition'
            );
            return token;
        } catch (error) {
            console.error('Failed to aquire teams token', error);
            return null;
        }
    }

    try {
        const response = await withTimeout(
            instance.acquireTokenSilent(request),
            SILENT_TOKEN_TIMEOUT_MS,
            'Silent Microsoft sign-in'
        );
        return response.accessToken;
    } catch (error) {
        console.error('Failed to acquire token silently', error);
        try {
            const response = await instance.acquireTokenPopup(request);
            return response.accessToken;
        } catch (popupError) {
            console.error('Failed to acquire token with popup', popupError);
        }
    }

    return null;
};

/**
 * Acquires an MSAL access token SILENTLY only — never falls back to a popup.
 * Used by the idle/background recovery path, where a popup cannot open without a
 * user gesture and would only fail. The still-valid Azure AD SSO cookie usually
 * lets acquireTokenSilent succeed without any UI. Returns null on failure;
 * onError receives the failure reason so callers can surface diagnostics.
 */
export const acquireMsalTokenSilent = async (
    clientId: string,
    {instance, accounts}: MSALInfo,
    onError?: (reason: string) => void
): Promise<string | null> => {
    if (accounts.length === 0) {
        onError?.('No signed-in Microsoft account found in this browser.');
        return null;
    }

    try {
        const response = await withTimeout(
            instance.acquireTokenSilent({
                scopes: [`${clientId}/.default`],
                account: accounts[0]
            }),
            SILENT_TOKEN_TIMEOUT_MS,
            'Silent Microsoft sign-in'
        );
        return response.accessToken;
    } catch (error) {
        console.error('Silent MSAL token acquisition failed', error);
        const errorCode =
            error && typeof error === 'object' && 'errorCode' in error
                ? String((error as {errorCode: unknown}).errorCode)
                : error instanceof Error
                  ? error.message
                  : String(error);
        onError?.(errorCode);
        return null;
    }
};

export default getAccessTokenImpl;
